import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { WeatherForecastDay, WeatherMetric } from '../../services/weatherService';

interface Props {
  forecast: WeatherForecastDay[];
}

type WeatherRange = 'today' | 'tomorrow' | 'next3' | 'week' | 'twoWeeks';

const metricOptions: Array<{ value: WeatherMetric; labelKey: string; defaultLabel: string; unit: string; color: string }> = [
  { value: 'windSpeed', labelKey: 'metricOptions.windSpeed', defaultLabel: 'Wind Speed', unit: ' km/h', color: '#06a77d' },
  { value: 'humidity', labelKey: 'metricOptions.humidity', defaultLabel: 'Humidity', unit: '%', color: '#2f8fe6' },
  { value: 'temperature', labelKey: 'metricOptions.temperature', defaultLabel: 'Temperature', unit: ' C', color: '#ef476f' },
  { value: 'rain', labelKey: 'metricOptions.rain', defaultLabel: 'Rain', unit: ' mm', color: '#7b61ff' },
  { value: 'pressure', labelKey: 'metricOptions.pressure', defaultLabel: 'Pressure', unit: ' hPa', color: '#f59f00' },
  { value: 'cloudCover', labelKey: 'metricOptions.cloudCover', defaultLabel: 'Cloud Cover', unit: '%', color: '#64748b' },
];

const rangeOptions: Array<{ value: WeatherRange; labelKey: string; defaultLabel: string; startOffset: number; dayCount: number }> = [
  { value: 'today', labelKey: 'rangeOptions.today', defaultLabel: 'Today', startOffset: 0, dayCount: 1 },
  { value: 'tomorrow', labelKey: 'rangeOptions.tomorrow', defaultLabel: 'Tomorrow', startOffset: 1, dayCount: 1 },
  { value: 'next3', labelKey: 'rangeOptions.next3', defaultLabel: 'Next 3 days', startOffset: 0, dayCount: 3 },
  { value: 'week', labelKey: 'rangeOptions.week', defaultLabel: 'Week', startOffset: 0, dayCount: 7 },
  { value: 'twoWeeks', labelKey: 'rangeOptions.twoWeeks', defaultLabel: '2 weeks', startOffset: 0, dayCount: 14 },
];

const toDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const startOfLocalDay = (date: Date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const addDays = (date: Date, days: number) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const WeatherChart: React.FC<Props> = ({ forecast }) => {
  const { t } = useTranslation();
  const [metric, setMetric] = useState<WeatherMetric>('windSpeed');
  const [range, setRange] = useState<WeatherRange>('today');
  const [selectedDate, setSelectedDate] = useState('');
  const activeMetric = metricOptions.find((option) => option.value === metric) ?? metricOptions[0];
  const activeRange = rangeOptions.find((option) => option.value === range) ?? rangeOptions[0];

  useEffect(() => {
    setSelectedDate('');
  }, [range]);

  const rangeFilteredForecast = useMemo(() => {
    const today = startOfLocalDay(new Date());
    const start = addDays(today, activeRange.startOffset);
    const end = addDays(start, activeRange.dayCount);

    return forecast.filter((item) => {
      const itemDate = new Date(item.date);
      return itemDate >= start && itemDate < end;
    });
  }, [activeRange.dayCount, activeRange.startOffset, forecast]);

  const dateOptions = useMemo(() => {
    const seen = new Set<string>();
    return rangeFilteredForecast
      .map((item) => toDateKey(new Date(item.date)))
      .filter((date) => {
        if (seen.has(date)) return false;
        seen.add(date);
        return true;
      });
  }, [rangeFilteredForecast]);

  const minDate = dateOptions[0] ?? '';
  const maxDate = dateOptions[dateOptions.length - 1] ?? '';

  const chartData = useMemo(() => {
    const filteredByDate = selectedDate
      ? rangeFilteredForecast.filter((item) => toDateKey(new Date(item.date)) === selectedDate)
      : rangeFilteredForecast;

    return filteredByDate
      .filter((item) => typeof item[metric] === 'number')
      .map((day) => ({
        ...day,
        label: new Date(day.date).toLocaleString(undefined, {
          month: !selectedDate && activeRange.dayCount > 1 ? 'short' : undefined,
          day: !selectedDate && activeRange.dayCount > 1 ? 'numeric' : undefined,
          hour: '2-digit',
          minute: '2-digit',
        }),
      }));
  }, [activeRange.dayCount, metric, rangeFilteredForecast, selectedDate]);

  return (
    <div className="weather-chart-panel">
      <div className="weather-chart-header">
        <h3>{t('dashboard.weather.forecast', { defaultValue: 'Forecast' })}</h3>
        <div className="weather-chart-controls">
          <select value={metric} onChange={(event) => setMetric(event.target.value as WeatherMetric)} className="weather-metric-select">
            {metricOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {t(`dashboard.weather.${option.labelKey}`, { defaultValue: option.defaultLabel })}
              </option>
            ))}
          </select>

          <select value={range} onChange={(event) => setRange(event.target.value as WeatherRange)} className="weather-metric-select">
            {rangeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {t(`dashboard.weather.${option.labelKey}`, { defaultValue: option.defaultLabel })}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={selectedDate}
            min={minDate}
            max={maxDate}
            onChange={(event) => setSelectedDate(event.target.value)}
            className="weather-metric-select"
          />
        </div>
      </div>

      <div className="weather-chart-container">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#667085', fontSize: 12 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#667085', fontSize: 12 }} width={34} />
              <Tooltip
                contentStyle={{ borderRadius: '8px', border: '1px solid #e0e0e0' }}
                formatter={(value: any) => [
                  `${value ?? '-'}${activeMetric.unit}`,
                  t(`dashboard.weather.${activeMetric.labelKey}`, { defaultValue: activeMetric.defaultLabel }),
                ]}
              />
              <Line type="monotone" dataKey={metric} stroke={activeMetric.color} strokeWidth={3} dot={false} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="weather-empty-state">{t('dashboard.weather.noForecast', { defaultValue: 'No forecast available.' })}</div>
        )}
      </div>
    </div>
  );
};

export default WeatherChart;
